<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  modelValue: { type: Object, default: null },
  query: { type: String, default: '' },
  label: { type: String, default: 'Buscar cliente' },
  helper: {
    type: String,
    default: 'Busque por nome, telefone, e-mail ou identificador.',
  },
  placeholder: { type: String, default: 'Nome, telefone ou e-mail do cliente' },
  required: { type: Boolean, default: false },
  recentContacts: { type: Array, default: () => [] },
  activeCount: { type: Number, default: 0 },
  activeLimit: { type: Number, default: 0 },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'update:query',
  'select',
  'error',
]);

const internalQuery = ref(props.query || '');
const contacts = ref([]);
const loading = ref(false);
const error = ref('');
let searchTimer = null;

const selectedId = computed(() => props.modelValue?.id || null);
const visibleRecentContacts = computed(() =>
  props.recentContacts
    .filter(contact => contact?.id && contact.id !== selectedId.value)
    .slice(0, 3)
);
const hasActiveLimit = computed(() => props.activeLimit > 0);
const activeLimitReached = computed(
  () => hasActiveLimit.value && props.activeCount >= props.activeLimit
);

const contactName = contact => contact?.name || 'Cliente sem nome';
const contactIdentity = contact =>
  [contact?.phone_number, contact?.email, contact?.identifier]
    .filter(Boolean)
    .join(' · ');
const contactMeta = contact => {
  const attrs =
    contact?.additional_attributes || contact?.additionalAttributes || {};
  return [attrs.operacao, attrs.canal, contact?.qualification_status]
    .filter(Boolean)
    .join(' · ');
};

const initialsFor = contact =>
  contactName(contact)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || 'CL';

const loadContacts = async () => {
  if (props.disabled) return;

  loading.value = true;
  error.value = '';

  try {
    const query = internalQuery.value.trim();
    const response = query
      ? await ContactAPI.search(query, 1, 'name')
      : await ContactAPI.get(1, 'name');
    contacts.value = response.data?.payload || [];
  } catch (exception) {
    contacts.value = [];
    error.value = 'Não foi possível buscar clientes agora.';
    emit('error', exception);
  } finally {
    loading.value = false;
  }
};

const scheduleSearch = () => {
  if (props.disabled) return;

  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(loadContacts, 250);
};

const selectContact = contact => {
  if (props.disabled) return;

  emit('update:modelValue', contact);
  emit('select', contact);
};

watch(
  () => props.query,
  value => {
    if (value !== internalQuery.value) {
      internalQuery.value = value || '';
    }
  }
);

watch(internalQuery, value => {
  emit('update:query', value);
  scheduleSearch();
});

onMounted(loadContacts);
onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-3">
    <label class="flex flex-col gap-1.5 text-[13px] font-redHatText">
      <span class="font-semibold text-lais-text-body leading-tight">
        {{ label }}
        <span v-if="required" class="text-lais-link">*</span>
      </span>
      <div class="flex min-w-0 gap-2">
        <input
          v-model="internalQuery"
          type="search"
          :placeholder="placeholder"
          :disabled="disabled"
          class="h-10 min-w-0 flex-1 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
          @keydown.enter.prevent="loadContacts"
        />
        <button
          type="button"
          class="grid h-10 w-10 shrink-0 place-items-center rounded-lais border border-lais-border bg-white text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
          :disabled="loading || disabled"
          title="Buscar clientes"
          @click="loadContacts"
        >
          <span
            :class="
              loading
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-search'
            "
            class="size-4"
            aria-hidden="true"
          />
        </button>
      </div>
      <span v-if="error" class="text-[12px] text-lais-danger leading-tight">
        {{ error }}
      </span>
      <span
        v-else-if="helper"
        class="text-[12px] text-lais-text-muted leading-tight"
      >
        {{ helper }}
      </span>
    </label>

    <div
      v-if="hasActiveLimit && selectedId"
      class="rounded-lais-sm border px-3 py-2 text-[12px] leading-relaxed"
      :class="
        activeLimitReached
          ? 'border-rose-200 bg-rose-50 font-semibold text-rose-700'
          : 'border-lais-border bg-lais-bg-soft text-lais-text-body'
      "
    >
      Este cliente tem {{ activeCount }}/{{ activeLimit }} visitas ativas no
      limite configurado da conta.
    </div>

    <section
      v-if="visibleRecentContacts.length && !internalQuery.trim()"
      class="grid gap-2"
    >
      <p
        class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
      >
        Últimos clientes agendados
      </p>
      <div class="grid gap-2 sm:grid-cols-3">
        <button
          v-for="contact in visibleRecentContacts"
          :key="contact.id"
          type="button"
          class="min-h-[68px] rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-left transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
          :disabled="disabled"
          @click="selectContact(contact)"
        >
          <span class="block truncate text-[13px] font-semibold text-lais-text">
            {{ contactName(contact) }}
          </span>
          <span class="mt-1 block truncate text-[12px] text-lais-text-muted">
            {{ contactIdentity(contact) || 'Sem telefone ou e-mail' }}
          </span>
        </button>
      </div>
    </section>

    <div class="grid max-h-[300px] gap-2 overflow-y-auto pr-1">
      <article
        v-for="contact in contacts"
        :key="contact.id"
        role="button"
        :tabindex="disabled ? -1 : 0"
        class="flex min-w-0 cursor-pointer items-start gap-3 rounded-lais border p-3 transition-colors"
        :class="
          disabled
            ? 'pointer-events-none border-lais-border bg-lais-bg-soft opacity-60'
            : selectedId === contact.id
              ? 'border-lais-primary bg-lais-primary-50'
              : 'border-lais-border bg-white hover:border-lais-primary'
        "
        @click="selectContact(contact)"
        @keydown.enter.prevent="selectContact(contact)"
      >
        <div
          class="grid size-11 shrink-0 place-items-center overflow-hidden rounded-full bg-lais-primary-50 text-[13px] font-bold text-lais-primary"
        >
          <img
            v-if="contact.thumbnail"
            :src="contact.thumbnail"
            :alt="contactName(contact)"
            class="h-full w-full object-cover"
          />
          <span v-else>{{ initialsFor(contact) }}</span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="break-words font-semibold text-lais-text">
            {{ contactName(contact) }}
          </p>
          <p class="break-words text-[12px] text-lais-text-body">
            {{
              contactIdentity(contact) || 'Sem telefone ou e-mail cadastrado'
            }}
          </p>
          <p
            v-if="contactMeta(contact)"
            class="mt-1 text-[12px] text-lais-text-muted"
          >
            {{ contactMeta(contact) }}
          </p>
        </div>
        <span
          v-if="selectedId === contact.id"
          class="i-lucide-check-circle-2 size-5 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
      </article>

      <div
        v-if="loading && !contacts.length"
        class="rounded-lais border border-lais-border p-4 text-center text-[13px] text-lais-text-muted"
      >
        Buscando clientes…
      </div>
      <div
        v-else-if="!loading && !contacts.length && !error"
        class="rounded-lais border border-lais-border p-4 text-center text-[13px] text-lais-text-muted"
      >
        Nenhum cliente encontrado para esta busca.
      </div>
    </div>
  </div>
</template>
